/**
 * teamWeekForecast.ts
 * Forward-looking TEAM capacity forecast for the Employee Schedule (Mike
 * Billings simplification — MIKE-SIMPLIFY.md PR3 / D3). Mike's real value is
 * cross-project capacity visibility: across ALL jobs, per week, "is everyone at
 * 80% over here — do I pull someone in for index testing, or hire?".
 *
 * This module rolls the per-(person, week) allocation cells the Employee
 * Schedule already loads into ONE compact read per week — team average load,
 * how many people are over capacity, how many still have room — plus a single
 * team band so a week can be flagged over / tight / well-under at a glance.
 *
 * PURE — no React / MUI / theme. Colours live in the components (theme.schedule.*
 * tokens) per CODING-STANDARDS; this module only decides the numbers + the band.
 * Structured so a future HISTORY view can reuse the same roll-up over past weeks
 * (history is deferred this pass — D3).
 */

/** Team-load classification for one week (drives the strip colour + week flag). */
export type TeamLoadBand = 'none' | 'under' | 'ok' | 'tight' | 'over';

/**
 * Band cut-points on the team's AVERAGE load (of the people who have work that
 * week). Kept here as the single tunable source, aligned with the per-person
 * allocation bands (scheduleAllocationBands): >100 over · ≥86 at/tight · <40
 * well-under (lots of slack).
 */
export const TEAM_FORECAST_THRESHOLDS = {
  /** avg < this (with people scheduled) → well under-staffed (has slack). */
  under: 40,
  /** avg ≥ this → tight (near capacity). */
  tight: 86,
  /** avg > this → over (team stretched). */
  over: 100,
  /** a person over this % is counted as "over capacity". */
  personOver: 100,
  /** a person under this % is counted as having meaningful "room". */
  personRoom: 50,
} as const;

/** A single (person, week) allocation cell — the only fields the roll-up needs. */
export interface TeamForecastCell {
  weekStart: string;
  /** Σ weekly allocation % across all this person's jobs (undefined ⇒ no data). */
  allocationPercent?: number;
}

/** The rolled-up forecast for ONE week across the whole loaded team. */
export interface TeamWeekForecast {
  weekStart: string;
  /** People with any load this week (the denominator for the average). */
  scheduledCount: number;
  /** Team average allocation % across the scheduled people (0 when none). */
  avgPercent: number;
  /** People over 100% — over-allocated (advisory; the crunch signal). */
  overCount: number;
  /** People with meaningful room (<50%) — candidates to pull work onto. */
  roomCount: number;
  /** Team-load band for the strip colour + week flag. */
  band: TeamLoadBand;
}

/** Round a finite number to an integer; non-finite ⇒ 0. */
function toInt(n: number | undefined): number {
  return typeof n === 'number' && Number.isFinite(n) ? Math.round(n) : 0;
}

/**
 * Classify a week's team average into a load band. `scheduledCount` gates the
 * 'none'/'under' verdicts — an empty week is 'none', not "well under".
 */
export function teamLoadBand(avgPercent: number, scheduledCount: number): TeamLoadBand {
  if (scheduledCount <= 0) return 'none';
  if (avgPercent > TEAM_FORECAST_THRESHOLDS.over) return 'over';
  if (avgPercent >= TEAM_FORECAST_THRESHOLDS.tight) return 'tight';
  if (avgPercent < TEAM_FORECAST_THRESHOLDS.under) return 'under';
  return 'ok';
}

/**
 * Roll per-(person, week) allocation cells into one TeamWeekForecast per week,
 * in the given `weeks` order (every requested week is present, even empty ones).
 *
 * The average is taken over the people who actually have load that week
 * (allocation > 0) so a large idle roster does not drag the number toward zero —
 * it answers "of the people working this week, how loaded are they".
 */
export function computeTeamWeekForecast(
  cells: readonly TeamForecastCell[],
  weeks: readonly string[],
): TeamWeekForecast[] {
  const byWeek = new Map<string, number[]>();
  for (const w of weeks) byWeek.set(w, []);
  for (const c of cells) {
    const list = byWeek.get(c.weekStart);
    if (!list) continue; // cell outside the requested window
    const p = toInt(c.allocationPercent);
    if (p > 0) list.push(p);
  }

  return weeks.map((weekStart) => {
    const loads = byWeek.get(weekStart) ?? [];
    const scheduledCount = loads.length;
    const sum = loads.reduce((a, b) => a + b, 0);
    const avgPercent = scheduledCount > 0 ? Math.round(sum / scheduledCount) : 0;
    const overCount = loads.filter((p) => p > TEAM_FORECAST_THRESHOLDS.personOver).length;
    const roomCount = loads.filter((p) => p < TEAM_FORECAST_THRESHOLDS.personRoom).length;
    return {
      weekStart,
      scheduledCount,
      avgPercent,
      overCount,
      roomCount,
      band: teamLoadBand(avgPercent, scheduledCount),
    };
  });
}

/**
 * A week is FLAGGED (worth Mike's attention) when the team is over/tight
 * (a crunch) or well-under (slack to redeploy). 'ok'/'none' weeks are unflagged.
 */
export function isFlaggedWeek(band: TeamLoadBand): boolean {
  return band === 'over' || band === 'tight' || band === 'under';
}

/** One-line human read of a week's forecast, e.g. "avg 78% · 2 over · 1 has room". */
export function teamForecastSummaryLabel(f: TeamWeekForecast): string {
  if (f.scheduledCount === 0) return 'no one scheduled';
  const parts = [`avg ${f.avgPercent}%`];
  if (f.overCount > 0) parts.push(`${f.overCount} over`);
  if (f.roomCount > 0) parts.push(`${f.roomCount} ${f.roomCount === 1 ? 'has' : 'have'} room`);
  return parts.join(' · ');
}

/** Short band word for tooltips / labels. */
export function teamLoadBandLabel(band: TeamLoadBand): string {
  switch (band) {
    case 'none':  return 'no one scheduled';
    case 'under': return 'well under capacity';
    case 'ok':    return 'balanced';
    case 'tight': return 'near capacity';
    case 'over':  return 'over capacity';
  }
}
